import React from 'react'
import styled from '@emotion/styled'
import useGenerateAppDownloadUrl from 'hooks/useGenerateAppDownloadUrl'
import {useGetAppRatingsQuery} from 'modules/appRatings'

import {Box} from '@vio/atlas/helpers/Box'
import {NoStyleLink} from '@vio/atlas/helpers/NoStyleLink'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {StarRating} from '@vio/atlas/StarRating'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {OneLinkURLs, Placement} from '@vio/core/src/deepLinking/types'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

const googlePlay = `/static/hero-section/hero-section-google-play.svg`
const appStore = `/static/hero-section/hero-section-apple-store.svg`

const StyledStarRating = styled(StarRating)`
  & svg {
    fill: ${cssTheme.colors.content.accent.inverse};
  }
`

interface Props {
  variant: 'GooglePlay' | 'AppStore'
  redirectLanguage?: string
}

/**
 * Truncates a number to one decimal place without rounding.
 *
 * @param num - The input number to truncate.
 * @returns The truncated number with one decimal place (no rounding applied).
 *
 * @example
 * truncateToOneDecimal(4.864); // 4.8
 * truncateToOneDecimal(4.899); // 4.8
 * truncateToOneDecimal(5.01);  // 5.0
 */
const truncateToOneDecimal = (num: number): number => {
  return Math.floor(num * 10) / 10
}

export const AppRatingsAndReviews: React.FC<Props> = ({
  variant,
  redirectLanguage
}) => {
  const {data, isLoading} = useGetAppRatingsQuery()
  const {generateAppStoreDeepLink, generatePlayStoreDeepLink} =
    useGenerateAppDownloadUrl()
  const isHydrated = useIsHydrated()

  if (isLoading)
    return (
      <Box display="flex" alignItems="center" gap="s300">
        <Box width="42px" height="42px">
          <SkeletonLoader height="100%" />
        </Box>
        <div>
          <SkeletonLoader width={68} />
          <SkeletonLoader width={105} />
        </div>
      </Box>
    )

  const isAppStore = variant === 'AppStore'
  const generateDeepLink = isAppStore
    ? generateAppStoreDeepLink
    : generatePlayStoreDeepLink
  const storeUrl = isAppStore ? OneLinkURLs.APP_STORE : OneLinkURLs.PLAY_STORE

  // The attributed URL needs AppsFlyer's browser-only script, so the server and
  // the hydration render share the plain OneLink and swap it in after mount.
  const appDownloadUrl = isHydrated
    ? generateDeepLink(
        Placement.HomePageHeroSection,
        undefined,
        redirectLanguage
      )
    : storeUrl

  if (variant === 'AppStore') {
    const {ios = {ratingScore: 5, ratingCount: 5000}} = data || {}
    return (
      <NoStyleLink
        href={appDownloadUrl}
        data-id="AppleAppStoreBadge"
        target="_blank"
      >
        <Box display="flex" alignItems="center" gap="s300">
          <img src={appStore} alt="app-store" />
          <Box
            display="flex"
            flexDirection="column"
            gap="s100"
            alignItems="flex-start"
          >
            <StyledStarRating size="xs" rating={Math.ceil(ios.ratingScore)} />
            <Text variant="bodyXS">
              <FormattedMessageWrapper
                id="appRatingReviews"
                defaultMessage="{rating} · {reviews, plural, one {1 review} other {# reviews}}"
                values={{
                  rating: truncateToOneDecimal(ios.ratingScore),
                  reviews: ios.ratingCount
                }}
              />
            </Text>
          </Box>
        </Box>
      </NoStyleLink>
    )
  }

  const {android = {ratingScore: 5, ratingCount: 5000}} = data || {}
  return (
    <NoStyleLink
      href={appDownloadUrl}
      data-id="GooglePlayBadge"
      target="_blank"
    >
      <Box display="flex" alignItems="center" gap="s300">
        <img src={googlePlay} alt="google-play" />
        <Box display="flex" flexDirection="column">
          <StyledStarRating size="xs" rating={Math.ceil(android.ratingScore)} />
          <Text variant="bodyXS">
            <FormattedMessageWrapper
              id="appRatingReviews"
              defaultMessage="{rating} · {reviews, plural, one {1 review} other {# reviews}}"
              values={{
                rating: truncateToOneDecimal(android.ratingScore),
                reviews: android.ratingCount
              }}
            />
          </Text>
        </Box>
      </Box>
    </NoStyleLink>
  )
}
